/** React */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

/** MUI icons */
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** MUI */
import { styled } from "@mui/material/styles";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

/** Component styles */
const PREFIX = "SubsSummary";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    summaryLabels: `${PREFIX}-summaryLabels`,
    summaryDetails: `${PREFIX}-summaryDetails`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 30px 30px !important",
    },

    [`& .${classes.summaryLabels}`]: {
        display: "flex",
        justifyContent: "space-between",
        padding: "24px 0px 8px",
        borderBottom: "1px solid #cccc",
    },

    [`& .${classes.summaryDetails}`]: {
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: "16px",
    },
}));

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)({
    boxShadow: "none",
    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#031851" }} />} {...props} />
))({
    "& .MuiAccordionSummary-content": {
        margin: "12px 0",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 20px 0px",
    "& .MuiCard-root": {
        boxShadow: "none",
    },
});

export const SubsSummary = (props) => {
    /** Properties */
    const { valuesSubsXero, valuesSubsBanking, valuesSubsReport, valuesSubsTaxes, valuesSubsCustomer } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [expanded, setExpanded] = useState(true);

    /** Component functions */
    const handleChangeSumaryAccordion = (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    return (
        <StyledContainer>
            <Card
                elevation={0}
                sx={{
                    mt: 3,
                    borderRadius: 8,
                }}
            >
                <CardContent sx={{ pt: 1 }}>
                    <Accordion
                        expanded={expanded}
                        onChange={handleChangeSumaryAccordion}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary
                            aria-controls="panel-content"
                            id="panel-header"
                            sx={{
                                borderBottom: "2px solid #ff5968",
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Typography
                                        variant="h2"
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Software subscription
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ pr: 1 }}>
                                    <Typography variant="h3">
                                        {`$${parseFloat(
                                            (!valuesSubsBanking.chip ? 0 : valuesSubsBanking.projectTotal) +
                                            (!valuesSubsReport.chip ? 0 : valuesSubsReport.projectTotal) +
                                            (!valuesSubsXero.chip ? 0 : valuesSubsXero.totalFee) +
                                            (!valuesSubsTaxes.chip ? 0 : valuesSubsTaxes.projectTotal) +
                                            (!valuesSubsCustomer.chip ? 0 : valuesSubsCustomer.projectTotal)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}/${t(`proposals.month`)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            {valuesSubsXero.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSubsXero.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Xero
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSubsXero.totalFee).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.month`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSubsXero.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSubsXero.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {t("proposals.basicService")}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSubsXero.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSubsXero.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.month")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                        {valuesSubsXero.xeroChecks && (
                                            <Grid item xs={11}>
                                                <Box className={classes.summaryDetails}>
                                                    <Typography
                                                        variant="h3"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto",
                                                            mb: "auto",
                                                            fontWeight: 700,
                                                            color: valuesSubsXero.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`${t(`proposals.addons`)}`} {/*(${valuesSubsXero.totalUsers} hrs)*/}
                                                    </Typography>
                                                    <Typography variant="body2" align="center" sx={{color: valuesSubsXero.isError? "#ff5968": "primary"}}>
                                                        {`$${parseFloat(valuesSubsXero.additionalsTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.month`)}`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSubsBanking.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSubsBanking.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Kiiper Banking
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSubsBanking.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.month`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSubsBanking.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSubsBanking.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {t("proposals.basicService")}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSubsBanking.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSubsBanking.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.month")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSubsTaxes.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSubsTaxes.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Kiiper Taxes
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSubsTaxes.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.month`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSubsTaxes.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSubsTaxes.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {t("proposals.basicService")}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSubsBanking.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSubsTaxes.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.month")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSubsReport.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSubsReport.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Kiiper Reporting
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSubsReport.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.month`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSubsReport.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSubsReport.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {t("proposals.basicService")}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSubsReport.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSubsReport.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.month")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSubsCustomer.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSubsCustomer.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Customer Support
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSubsCustomer.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.month`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSubsCustomer.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSubsCustomer.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesSubsCustomer.hours} ${t(
                                                        "miscellaneous.hour"
                                                    )})`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSubsCustomer.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSubsCustomer.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.month")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </StyledContainer>
    );
};
